// Network Manager for handling online/offline status and request retries
import offlineStorage from './offlineStorage';

class NetworkManager {
  constructor() {
    this.isOnline = navigator.onLine;
    this.retryQueue = [];
    this.isRetrying = false;
    this.listeners = [];
    this.maxRetries = 3;
    this.baseDelay = 1000; // 1 second
    this.maxDelay = 30000; // 30 seconds
    
    this.setupEventListeners();
    this.init();
  }

  async init() {
    try {
      await offlineStorage.init();
      console.log('📱 Network Manager initialized');
    } catch (error) {
      console.error('❌ Failed to initialize Network Manager:', error);
    }
  }

  setupEventListeners() {
    window.addEventListener('online', () => {
      console.log('🌐 Network connection restored');
      this.isOnline = true;
      this.notifyListeners('online');
      this.processRetryQueue();
    });

    window.addEventListener('offline', () => {
      console.log('📴 Network connection lost');
      this.isOnline = false;
      this.notifyListeners('offline');
    });
  }

  addListener(callback) {
    this.listeners.push(callback);
    return () => {
      this.listeners = this.listeners.filter(listener => listener !== callback);
    };
  }

  notifyListeners(event) {
    this.listeners.forEach(listener => {
      try {
        listener(event, this.isOnline);
      } catch (error) {
        console.error('Error in network listener:', error);
      }
    });
  }

  async makeRequest(requestConfig, retryConfig = {}) {
    const {
      maxRetries = this.maxRetries,
      retryDelay = this.baseDelay,
      storeOnFailure = true,
      endpoint = 'unknown'
    } = retryConfig;

    try {
      if (!this.isOnline) {
        throw new Error('No internet connection');
      }

      const response = await this.executeRequest(requestConfig);
      return response;
    } catch (error) {
      console.error(`❌ Request failed for ${endpoint}:`, error);
      
      if (storeOnFailure && this.shouldRetry(error)) {
        await this.storeForRetry(requestConfig, retryConfig, error);
      }
      
      throw error;
    }
  }

  async executeRequest(requestConfig) {
    const { method = 'GET', url, data, headers = {}, ...config } = requestConfig;
    
    const fetchConfig = {
      method,
      headers: {
        'Content-Type': 'application/json',
        ...headers
      },
      ...config
    };

    if (data && method !== 'GET') {
      if (data instanceof FormData) {
        delete fetchConfig.headers['Content-Type']; // Let browser set it for FormData
        fetchConfig.body = data;
      } else {
        fetchConfig.body = JSON.stringify(data);
      }
    }

    const response = await fetch(url, fetchConfig);
    
    console.log(`📡 Response status: ${response.status} ${response.statusText}`);
    console.log(`📡 Response headers:`, Object.fromEntries(response.headers.entries()));
    
    if (!response.ok) {
      // Intentar obtener el cuerpo de la respuesta para más detalles del error
      let errorBody = '';
      try {
        const responseText = await response.text();
        errorBody = responseText;
        console.error(`❌ Error response body:`, responseText);
      } catch (e) {
        console.error('❌ Could not read error response body');
      }
      
      throw new Error(`HTTP ${response.status}: ${response.statusText}${errorBody ? ` - ${errorBody}` : ''}`);
    }

    return response;
  }

  shouldRetry(error) {
    // Don't retry for certain types of errors
    if (error.message.includes('401') || error.message.includes('403')) {
      return false; // Authentication errors
    }
    
    if (error.message.includes('404')) {
      return false; // Not found errors
    }
    
    if (error.message.includes('400')) {
      return false; // Bad request errors
    }

    return true; // Retry for network errors, timeouts, 5xx errors
  }

  async storeForRetry(requestConfig, retryConfig, error) {
    try {
      const retryData = {
        requestConfig,
        retryConfig,
        error: error.message,
        endpoint: retryConfig.endpoint || 'unknown',
        timestamp: Date.now(),
        retryCount: 0
      };

      await offlineStorage.storeFailedRequest(retryData);
      console.log(`📦 Stored failed request for retry: ${retryConfig.endpoint}`);
    } catch (storageError) {
      console.error('❌ Failed to store request for retry:', storageError);
    }
  }

  async processRetryQueue() {
    if (this.isRetrying || !this.isOnline) {
      return;
    }

    this.isRetrying = true;
    console.log('🔄 Processing retry queue...');

    try {
      const failedRequests = await offlineStorage.getFailedRequests();
      
      for (const failedRequest of failedRequests) {
        if (failedRequest.retryCount >= this.maxRetries) {
          console.log(`❌ Max retries reached for ${failedRequest.endpoint}, removing from queue`);
          await offlineStorage.removeFailedRequest(failedRequest.id);
          continue;
        }

        try {
          const response = await this.executeRequest(failedRequest.requestConfig);
          console.log(`✅ Retry successful for ${failedRequest.endpoint}`);
          await offlineStorage.removeFailedRequest(failedRequest.id);
        } catch (error) {
          console.log(`❌ Retry failed for ${failedRequest.endpoint}:`, error.message);
          
          const newRetryCount = failedRequest.retryCount + 1;
          await offlineStorage.updateRetryCount(failedRequest.id, newRetryCount);
          
          // Add delay between retries
          await this.delay(this.calculateRetryDelay(newRetryCount));
        }
      }
    } catch (error) {
      console.error('❌ Error processing retry queue:', error);
    } finally {
      this.isRetrying = false;
    }
  }

  calculateRetryDelay(retryCount) {
    // Exponential backoff with jitter
    const exponentialDelay = this.baseDelay * Math.pow(2, retryCount - 1);
    const jitter = Math.random() * 0.1 * exponentialDelay; // 10% jitter
    return Math.min(exponentialDelay + jitter, this.maxDelay);
  }

  delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  async retryFailedRequests() {
    if (!this.isOnline) {
      throw new Error('Cannot retry requests while offline');
    }
    
    await this.processRetryQueue();
  }

  async getPendingRequestsCount() {
    try {
      const failedRequests = await offlineStorage.getFailedRequests();
      return failedRequests.length;
    } catch (error) {
      console.error('❌ Error getting pending requests count:', error);
      return 0;
    }
  }

  async clearAllPendingRequests() {
    try {
      await offlineStorage.clearAll();
      console.log('🗑️ Cleared all pending requests');
    } catch (error) {
      console.error('❌ Error clearing pending requests:', error);
    }
  }

  getNetworkStatus() {
    return {
      isOnline: this.isOnline,
      isRetrying: this.isRetrying
    };
  }
}

export default new NetworkManager();
